/* ==========================================================================
   Ebru Booking Widget — the water-marbling workshops' own calendar
   --------------------------------------------------------------------------
   A COPY of mas-booking-widget.js, deliberately forked rather than shared:
   each workshop type owns its booking widget, so changing how ebru books
   cannot move the mosaic pages. Fixes that apply to both have to be made in
   both — see docs/ebru-template-pair.md.

   What differs from the mosaic widget today:
     • the pop-up header is the product name over the studio, not "Book your
       workshop" over the product name
     • the projects step asks "decide at the studio" (first) or "choose now",
       and keeps the project list hidden until "choose now" is picked
     • deciding at the studio gets its own seat stepper, because the simple
       flow otherwise counts seats from project quantities
     • there is no separate review step: the receipt and Checkout live under
       the picker, since the address is in the sticky header
   --------------------------------------------------------------------------
   MAS Booking Widget — custom calendar for BookThatApp-powered workshops
   --------------------------------------------------------------------------
   A theme-owned replacement for the BookThatApp (BTA) product widget UI.
   BTA still owns the data: availability is read from, and reservations are
   written to, BTA's own endpoints through the Shopify App Proxy that the app
   installs at /apps/bookthatapp (same-origin, no API token required — this is
   exactly what BTA's own iframe widget does).

   The flow is a vertical stepper. One step is open at a time; answering it
   folds it into a bar that shows the decision (with "Change" to reopen) and
   opens the next step. Changing an earlier answer re-checks everything
   below it. Two flows share the code (config "flow"):
     full    date & time -> who's coming -> projects (now / at the studio) -> review
     simple  date & time -> projects + companions -> review
   The widget renders inline; the first tap on any of its controls
   lifts it into a pop-up (full screen on phones, a centred dialog on
   desktop) so the visitor stays in the booking flow. Closing the pop-up
   keeps every choice and drops the widget back where it was.

     1. Date & time    calendar (seats left per day) -> sessions for that day
     2. Who's coming   how many people -> "will anyone create together?"
                       (a companion shares a maker's project: the product's
                       "Accompany Person" variant) -> how many companions
     3. Projects       choose now (one project per maker) or decide at the
                       studio (the product's "Decide on spot" variant)
     4. Review         receipt + Checkout

   Data flow (mirrors BTA's own widget so BTA still converts the order into a
   booking):

     GET  /apps/bookthatapp/api/v1/frames?external_id=&start=&finish=&shop=
          -> dates + timeslots + per-variant availability
     POST /apps/bookthatapp/api/v1/reservations   (holds the seats)
     POST /cart/update.js  attributes["BTA Token"] = reservation.cart_token
     POST /cart/add.js     one line per variant with the same line-item
          properties BTA writes: Date, Time, _date_iso8601, _addons_uuid
     redirect to /checkout (or /cart)

   Everything is scoped under the root element the snippet renders
   (`[data-ebru-booking]`), no globals other than window.EbruBooking.
   ========================================================================== */
(function () {
  'use strict';

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var CHIP_LIMIT = 8;   // people chips 1..8, then a "9+" chip that opens a stepper
  var STAGES = ['date', 'people', 'projects'];

  /* ---------- small helpers ------------------------------------------- */

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  var ICONS = {
    date: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/></svg>',
    time: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'
  };
  // Constant markup from ICONS only — nothing here is ever user or API data.

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  // Replaces [key], {key} or {{key}} in editor copy.
  function fill(str, key, val) {
    return String(str == null ? '' : str).replace(new RegExp('\\[' + key + '\\]|\\{\\{\\s*' + key + '\\s*\\}\\}|\\{' + key + '\\}', 'g'), String(val));
  }
  // Sessions that reach one of these are worth watching back, so Clarity is
  // asked to prioritise recording them rather than sampling them away.
  var UPGRADE_EVENTS = { checkout_clicked: 1, booking_added_to_cart: 1 };

  function track(evt, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: evt };
      if (data) for (var k in data) payload[k] = data[k];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break booking */ }
    try {
      /* Same funnel into Microsoft Clarity, which does not read dataLayer.
         The stub is Clarity's own queueing shape (see custom.js); defining it
         here means events fired before the tag loads are replayed, not lost.
         Prefixed so they sort together in Clarity's event list. */
      window.clarity = window.clarity || function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
      window.clarity('event', 'mas_' + evt);
      if (UPGRADE_EVENTS[evt]) window.clarity('upgrade', evt);
    } catch (e) { /* analytics must never break booking */ }
  }
  function money(cents, format) {
    var v = (cents / 100).toFixed(2);
    var whole = v.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var str = whole + '.' + v.split('.')[1];
    return (format || '${{amount}}').replace('{{amount}}', str);
  }
  function encodeForm(obj, prefix, out) {
    // qs-compatible (indices array format) — what BTA's widget posts.
    out = out || [];
    Object.keys(obj).forEach(function (k) {
      var v = obj[k];
      var key = prefix ? prefix + '[' + k + ']' : k;
      if (v === undefined) return;
      if (v === null) { out.push(encodeURIComponent(key) + '='); return; }
      if (Array.isArray(v) || (typeof v === 'object')) { encodeForm(v, key, out); return; }
      out.push(encodeURIComponent(key) + '=' + encodeURIComponent(v));
    });
    return out.join('&');
  }
  function reducedMotion() {
    try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  /* ---------- timezone-aware formatting (no dayjs needed) --------------- */

  function partsIn(date, tz) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', weekday: 'short'
    });
    var out = {};
    fmt.formatToParts(date).forEach(function (p) { out[p.type] = p.value; });
    var h = parseInt(out.hour, 10); if (h === 24) h = 0;
    return {
      year: parseInt(out.year, 10), month: parseInt(out.month, 10), day: parseInt(out.day, 10),
      hour: h, minute: parseInt(out.minute, 10), weekday: out.weekday
    };
  }
  function ymd(p) { return p.year + '-' + pad2(p.month) + '-' + pad2(p.day); }
  // BTA's "Date" property: MM/DD/YYYY in the location's timezone.
  function btaDate(iso, tz) { var p = partsIn(new Date(iso), tz); return pad2(p.month) + '/' + pad2(p.day) + '/' + p.year; }
  // BTA's "Time" property with time_format=casual: "hh:mma" lowercase, no space (e.g. 11:00am, 06:30pm).
  function btaTime(iso, tz) {
    var p = partsIn(new Date(iso), tz);
    var h12 = p.hour % 12; if (h12 === 0) h12 = 12;
    return pad2(h12) + ':' + pad2(p.minute) + (p.hour < 12 ? 'am' : 'pm');
  }
  // Human time for the UI: "11:00 AM"
  function niceTime(iso, tz) {
    var p = partsIn(new Date(iso), tz);
    var h12 = p.hour % 12; if (h12 === 0) h12 = 12;
    return h12 + ':' + pad2(p.minute) + ' ' + (p.hour < 12 ? 'AM' : 'PM');
  }
  function niceDate(iso, tz) {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(iso));
  }
  function shortDate(iso, tz) {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso));
  }
  // First instant of a calendar month in tz, as ISO (used for the frames window).
  function monthBoundsISO(year, month /* 1-12 */, tz) {
    // Build "local midnight" by probing: start from UTC midnight and shift by the tz offset at that moment.
    function localToUTC(y, m, d) {
      var guess = Date.UTC(y, m - 1, d, 0, 0, 0);
      var p = partsIn(new Date(guess), tz);
      var asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
      var offset = asIfUTC - guess; // tz offset at that instant
      return new Date(guess - offset);
    }
    var start = localToUTC(year, month, 1);
    var nextY = month === 12 ? year + 1 : year, nextM = month === 12 ? 1 : month + 1;
    var end = new Date(localToUTC(nextY, nextM, 1).getTime() - 1000);
    return { start: start.toISOString(), finish: end.toISOString() };
  }

  /* ---------- BTA proxy client ---------------------------------------- */

  function BtaClient(cfg) {
    this.base = cfg.proxyPrefix || '/apps/bookthatapp';
    this.shop = cfg.shop; // e.g. "mosaic-art-studio-us"
  }
  // A dead availability request used to leave .masbk__loading — which covers
  // the whole card — spinning forever. This is the backstop for that, and
  // nothing more.
  //
  // Deliberately generous. The endpoint answers in ~0.4s, but the browser caps
  // connections per host, so a perfectly healthy request can sit queued behind
  // whatever else the page is loading: measured at 11s in a local theme-dev
  // preview. Earlier attempts at 8s and 15s aborted exactly such a request and
  // sent the calendar back to an empty month — a worse failure than the hang
  // being guarded against. 30s cannot false-positive on a live request; it only
  // ever catches one that is never coming back.
  var FRAMES_TIMEOUT_MS = 30000;

  BtaClient.prototype.frames = function (externalId, startISO, finishISO) {
    var url = this.base + '/api/v1/frames?' + encodeForm({
      external_id: externalId, start: startISO, finish: finishISO, shop: this.shop
    });
    var opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = null;
    if (ctrl) {
      opts.signal = ctrl.signal;
      timer = setTimeout(function () { ctrl.abort(); }, FRAMES_TIMEOUT_MS);
    }
    var clear = function () { if (timer) { clearTimeout(timer); timer = null; } };
    return fetch(url, opts).then(function (r) {
      clear();
      if (!r.ok) throw new Error('frames HTTP ' + r.status);
      return r.json();
    }, function (e) {
      clear();
      throw (e && e.name === 'AbortError') ? new Error('frames timed out') : e;
    });
  };
  // Which variants BookThatApp will actually sell. Hiding a variant in BTA's
  // product settings sets `ignore` on it here — the frames feed does NOT carry
  // that flag (it reports every variant as status 1 with the full pool), which
  // is why a variant hidden for stock control was still bookable in this
  // widget. external_id[] is the one filter form the endpoint honours; without
  // it the response is the whole catalogue, 25 products at a time.
  BtaClient.prototype.ignoredVariants = function (externalId) {
    var url = this.base + '/api/v1/products?' + encodeForm({ shop: this.shop }) +
      '&external_id[]=' + encodeURIComponent(externalId);
    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('products HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        var out = {};
        ((data && data.products) || []).forEach(function (p) {
          if (String(p.external_id) !== String(externalId)) return;
          (p.variants || []).forEach(function (v) { if (v.ignore) out[String(v.external_id)] = true; });
        });
        return out;
      });
  };

  BtaClient.prototype.reserve = function (cartToken, items) {
    var body = encodeForm({ cart_token: cartToken, items: items, shop: this.shop });
    return fetch(this.base + '/api/v1/reservations', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: body
    }).then(function (r) {
      return r.text().then(function (t) {
        var data = null; try { data = t ? JSON.parse(t) : null; } catch (e) { data = null; }
        if (!r.ok) { var err = new Error('reservation HTTP ' + r.status); err.status = r.status; err.data = data; throw err; }
        return data;
      });
    });
  };

  /* ---------- Shopify cart helpers ------------------------------------- */

  function cartGet() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }
  function cartSetAttributes(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { if (!r.ok) throw new Error('cart/update HTTP ' + r.status); return r.json(); });
  }
  function cartAdd(item) {
    var fd = new FormData();
    fd.append('id', item.id);
    fd.append('quantity', item.quantity);
    Object.keys(item.properties).forEach(function (k) { fd.append('properties[' + k + ']', item.properties[k]); });
    return fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) { var e = new Error(j && (j.description || j.message) || ('cart/add HTTP ' + r.status)); e.data = j; throw e; }
          return j;
        });
      });
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  function sha256Hex(str) {
    if (window.crypto && crypto.subtle && window.TextEncoder) {
      return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then(function (buf) {
        return Array.prototype.map.call(new Uint8Array(buf), function (b) { return pad2(b.toString(16)); }).join('');
      });
    }
    var s = ''; for (var i = 0; i < 64; i++) s += (Math.random() * 16 | 0).toString(16);
    return Promise.resolve(s);
  }

  /* ---------- availability model -------------------------------------- */

  // Seats a single variant can still take in a timeslot, and the shared pool
  // for the whole session. Works for all three BTA capacity modes:
  //   product-level  -> every variant reports the same `available`
  //   variant-level  -> per-variant `available`
  //   resource-level -> `resource_availability` is the shared room capacity
  function slotSeats(slot) {
    var pool = null;
    if (typeof slot.resource_availability === 'number') pool = slot.resource_availability;
    var perVariant = {};
    var anyOpen = false, maxAvail = 0;
    (slot.availability || []).forEach(function (a) {
      var n = (a.status === 1) ? (a.available || 0) : 0;
      if (pool !== null) n = Math.min(n, pool);
      perVariant[String(a.external_id)] = n;
      if (n > 0) anyOpen = true;
      if (n > maxAvail) maxAvail = n;
    });
    if (pool === null) pool = maxAvail;
    return { pool: pool, perVariant: perVariant, open: anyOpen && pool > 0 };
  }

  /* ---------- the widget ---------------------------------------------- */

  function Widget(root) {
    this.root = root;
    var cfgNode = qs(root, '[data-mas-booking-config]');
    this.cfg = JSON.parse(cfgNode.textContent);
    this.tz = this.cfg.timezone || 'America/New_York';
    this.simple = this.cfg.flow === 'simple';
    this.stageOrder = this.simple ? ['date', 'projects'] : STAGES;
    this.client = new BtaClient(this.cfg);
    this.monthCache = {};       // "YYYY-MM" -> { frames: [...], nextOccurrence }
    this.slotsByDate = {};      // "YYYY-MM-DD" -> [slot]
    this.state = {
      view: null,               // {year, month}
      selectedDate: null,
      selectedSlot: null,
      people: null,             // everyone attending, companions included
      peopleMore: false,        // "9+" chip opened the stepper
      together: null,           // null = not answered yet, true / false
      companions: null,         // null = not chosen yet
      mode: null,               // 'now' | 'later' | null
      qty: {},                  // variantId -> qty (choose-now)
      decideQty: 0,             // simple flow: seats on the decide-at-the-studio option
      open: 'date',             // the step that is unfolded
      showAllProjects: false,
      busy: false,
      error: null,
      interacted: false,
      firstOccurrence: null,
      lastOccurrence: null,
      reachAhead: 0            // furthest month we opened on; widens the arrows' ceiling
    };
    var vs = this.cfg.variants || [];
    this.projects = vs.filter(function (v) { return v.role === 'project'; });
    // Best sellers first (config "featured": titles in order), the rest in variant order.
    var featured = (this.cfg.featured || []).map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
    if (featured.length) {
      var rank = function (v) { var i = featured.indexOf(String(v.title).trim().toLowerCase()); return i < 0 ? featured.length : i; };
      this.projects = this.projects.map(function (v, i) { return { v: v, i: i }; })
        .sort(function (a, b) { return rank(a.v) - rank(b.v) || a.i - b.i; })
        .map(function (x) { return x.v; });
    }
    this.companionVariant = vs.filter(function (v) { return v.role === 'companion'; })[0] || null;
    this.decideVariant = vs.filter(function (v) { return v.role === 'decide'; })[0] || null;
    this.ui = {
      calendar: qs(root, '[data-mas-cal]'),
      monthLabel: qs(root, '[data-mas-month-label]'),
      prev: qs(root, '[data-mas-prev]'),
      next: qs(root, '[data-mas-next]'),
      jump: qs(root, '[data-mas-jump]'),
      empty: qs(root, '[data-mas-empty]'),
      pickDate: qs(root, '[data-mas-pick-date]'),
      slots: qs(root, '[data-mas-slots]'),
      slotsTitle: qs(root, '[data-mas-slots-title]'),
      peopleChips: qs(root, '[data-mas-people-chips]'),
      peopleStep: qs(root, '[data-mas-people-step]'),
      peopleStepVal: qs(root, '[data-mas-people-val]'),
      peopleMinus: qs(root, '[data-mas-people-minus]'),
      peoplePlus: qs(root, '[data-mas-people-plus]'),
      peopleContinue: qs(root, '[data-mas-people-continue]'),
      togetherHint: qs(root, '[data-mas-together-hint]'),
      companionChips: qs(root, '[data-mas-companion-chips]'),
      peopleSplit: qs(root, '[data-mas-people-split]'),
      modeLaterHint: qs(root, '[data-mas-mode-later-hint]'),
      progressTitle: qs(root, '[data-mas-progress-title]'),
      progress: qs(root, '[data-mas-progress]'),
      projects: qs(root, '[data-mas-projects]'),
      more: qs(root, '[data-mas-more]'),
      companionRow: qs(root, '[data-mas-companion-row]'),
      decideRow: qs(root, '[data-mas-decide-row]'),
      offerNudge: qs(root, '[data-mas-offer-nudge]'),
      offerNudgeText: qs(root, '[data-mas-nudge-text]'),
      offerNudgeSub: qs(root, '[data-mas-nudge-sub]'),
      offerTiers: qsa(root, '[data-mas-offer-tier]'),
      receipt: qs(root, '[data-mas-receipt]'),
      checkout: qs(root, '[data-mas-checkout]'),
      fine: qs(root, '[data-mas-fine]'),
      error: qs(root, '[data-mas-error]'),
      loading: qs(root, '[data-mas-loading]')
    };
    // Every part of the flow that appears / disappears is a "block".
    this.blocks = {};
    var self = this;
    qsa(root, '[data-mas-block]').forEach(function (n) { self.blocks[n.getAttribute('data-mas-block')] = n; });
    // The four steps: header bar (button), summary text, "Change" label, body.
    this.stages = {};
    qsa(root, '[data-mas-stage]').forEach(function (li) {
      self.stages[li.getAttribute('data-mas-stage')] = {
        el: li, head: qs(li, '[data-mas-stage-head]'), body: qs(li, '[data-mas-body]'),
        sum: qs(li, '[data-mas-sum]'), edit: qs(li, '[data-mas-edit]'),
        ask: qs(li, '[data-mas-stage-ask]')
      };
    });
    this.seen = {};             // blocks revealed at least once (analytics)
    this.lastOpen = null;
    this.lastRevealed = null;   // block that just appeared (what to bring into view)
    this.modal = null;          // built on first use
    this.modalOpen = false;
    this.placeholder = null;    // marks the inline spot while the widget is in the pop-up
    this.booted = false;        // no animations / scrolling for the first render
    this.bind();
    this.init();
  }

  Widget.prototype.bind = function () {
    var self = this;
    this.ui.prev.addEventListener('click', function () { self.shiftMonth(-1); });
    this.ui.next.addEventListener('click', function () { self.shiftMonth(1); });
    this.ui.jump.addEventListener('click', function () { self.jumpToNextAvailable(); });
    qsa(this.root, '[data-mas-together]').forEach(function (b) {
      b.addEventListener('click', function () { self.onPickTogether(b.getAttribute('data-mas-together') === 'yes'); });
    });
    qsa(this.root, '[data-mas-mode]').forEach(function (b) {
      b.addEventListener('click', function () { self.onPickMode(b.getAttribute('data-mas-mode')); });
    });
    if (this.ui.peopleMinus) this.ui.peopleMinus.addEventListener('click', function () { self.onPickPeople((self.state.people || 0) - 1, true); });
    if (this.ui.peoplePlus) this.ui.peoplePlus.addEventListener('click', function () { self.onPickPeople((self.state.people || 0) + 1, true); });
    if (this.ui.peopleContinue) this.ui.peopleContinue.addEventListener('click', function () { if (self.peopleDone()) { self.advance(); self.sync(); } });
    this.ui.checkout.addEventListener('click', function () { self.submit(); });
    this.ui.more.addEventListener('click', function () {
      self.state.showAllProjects = true;
      self.applyProjectsCollapse();
      if (self.modalOpen) self.follow();
    });
    Object.keys(this.stages).forEach(function (name) {
      self.stages[name].head.addEventListener('click', function () { self.onStageHead(name); });
    });
    this.root.addEventListener('click', function () {
      if (!self.state.interacted) { self.state.interacted = true; track('calendar_interaction'); }
    });
    this.root.addEventListener('click', function (e) {
      if (!self.cfg.popup || self.modalOpen || !self.booted) return;
      var btn = e.target && e.target.closest ? e.target.closest('button') : null;
      if (btn && !btn.disabled) self.openModal();
    }, true);
  };

  Widget.prototype.setBusy = function (b) {
    this.state.busy = b;
    this.root.classList.toggle('is-busy', !!b);
    this.ui.loading.hidden = !b;
    this.ui.checkout.disabled = !!b || !this.complete();
  };
  Widget.prototype.setError = function (msg) {
    this.state.error = msg;
    this.ui.error.hidden = !msg;
    this.ui.error.textContent = msg || '';
    if (msg) this.ui.error.focus && this.ui.error.focus();
  };

  /* ---------- derived state ------------------------------------------- */

  // Seat numbers show only when they are getting scarce (config "seatsShowBelow",
  // 0 = always); other open days and sessions just read as available.
  Widget.prototype.showCount = function (n) {
    if (!this.cfg.showSeats) return false;
    var below = this.cfg.seatsShowBelow;
    return !(below > 0) || n < below;
  };
  Widget.prototype.variantById = function (id) {
    return (this.cfg.variants || []).filter(function (v) { return String(v.id) === String(id); })[0] || null;
  };
  // Seats a variant can still take in the selected session (0 = not bookable).
  Widget.prototype.variantMax = function (v, seats) {
    if (!v || !v.available) return 0;
    var m = seats.perVariant[String(v.id)];
    return m === undefined ? 0 : m;                  // undefined: BTA does not schedule this variant
  };
  Widget.prototype.companionFor = function (slot) {
    var v = this.companionVariant;
    return (v && slot && this.variantMax(v, slotSeats(slot)) > 0) ? v : null;
  };
  Widget.prototype.decideFor = function (slot) {
    var v = this.decideVariant;
    return (v && slot && this.variantMax(v, slotSeats(slot)) > 0) ? v : null;
  };
  // Everyone the session can still take: the room's availability is the only cap.
  Widget.prototype.peopleMax = function () {
    var slot = this.state.selectedSlot; if (!slot) return 0;
    return Math.max(0, slotSeats(slot).pool);
  };
  Widget.prototype.togetherApplies = function () {
    var s = this.state;
    return !!s.selectedSlot && s.people >= 2 && !!this.companionFor(s.selectedSlot);
  };
  Widget.prototype.companionMax = function () {
    var s = this.state, c = this.companionFor(s.selectedSlot);
    if (!c) return 0;
    var seats = slotSeats(s.selectedSlot);
    if (this.simple) return Math.max(0, Math.min(seats.pool - this.makers(), this.variantMax(c, seats)));
    if (!s.people) return 0;
    return Math.min(s.people - 1, this.variantMax(c, seats));
  };
  Widget.prototype.companions = function () {
    var s = this.state;
    if (this.simple) return Math.max(0, Math.min(s.companions || 0, this.companionMax()));
    if (!this.togetherApplies() || s.together !== true) return 0;
    if (s.people === 2) return 1;
    return s.companions || 0;
  };
  Widget.prototype.companionsDone = function () {
    var s = this.state;
    if (this.simple) return true;
    if (!this.togetherApplies()) return true;
    if (s.together === null) return false;
    if (s.together === false) return true;
    return s.people === 2 || (s.companions != null && s.companions >= 1 && s.companions <= this.companionMax());
  };
  Widget.prototype.peopleDone = function () {
    var s = this.state;
    if (this.simple) return !!s.selectedSlot;
    return !!s.selectedSlot && !!s.people && this.companionsDone();
  };
  // Seats being booked for themselves. In the simple flow the quantities say
  // it: either the projects picked, or the seats held to decide at the studio.
  Widget.prototype.makers = function () {
    if (!this.simple) return (this.state.people || 0) - this.companions();
    return this.effectiveMode() === 'later' ? this.decideSeats() : this.chosen();
  };
  // Everyone attending: in the simple flow that is the makers plus the companions.
  Widget.prototype.peopleCount = function () { return this.simple ? this.makers() + this.companions() : (this.state.people || 0); };
  Widget.prototype.chosen = function () {
    var q = this.state.qty, n = 0; Object.keys(q).forEach(function (k) { n += q[k]; }); return n;
  };
  // 'now' | 'later' | null. Without a bookable "decide" variant the only mode
  // is 'now' — a product that does not sell the seat cannot offer to hold one.
  Widget.prototype.effectiveMode = function () {
    return this.decideFor(this.state.selectedSlot) ? this.state.mode : 'now';
  };
  // Seats on the decide option, clamped to what the room can still take.
  Widget.prototype.decideSeats = function () {
    var s = this.state;
    if (!this.simple || this.effectiveMode() !== 'later') return 0;
    return Math.max(0, Math.min(s.decideQty || 0, this.decideMax()));
  };
  Widget.prototype.decideMax = function () {
    var s = this.state, d = this.decideFor(s.selectedSlot);
    if (!d) return 0;
    var seats = slotSeats(s.selectedSlot);
    return Math.max(0, Math.min(seats.pool - this.companions(), this.variantMax(d, seats)));
  };
  Widget.prototype.complete = function () {
    if (this.simple) return !!this.state.selectedSlot && this.makers() >= 1;
    if (!this.peopleDone() || this.makers() < 1) return false;
    var mode = this.effectiveMode();
    if (mode === 'later') return true;
    if (mode === 'now') return this.chosen() === this.makers();
    return false;
  };
  // Cart lines for the current answers: [{ id, quantity, variant }]
  Widget.prototype.lines = function () {
    var s = this.state, self = this, out = [];
    if (this.effectiveMode() === 'now') {
      Object.keys(s.qty).forEach(function (id) {
        if (s.qty[id] > 0) out.push({ id: String(id), quantity: s.qty[id], variant: self.variantById(id) });
      });
    } else {
      var d = this.decideFor(s.selectedSlot);
      if (d) out.push({ id: String(d.id), quantity: this.makers(), variant: d });
    }
    var c = this.companions();
    if (c > 0) { var cv = this.companionFor(s.selectedSlot); if (cv) out.push({ id: String(cv.id), quantity: c, variant: cv }); }
    return out;
  };

  /* ---------- group offer ---------------------------------------------
     A PREVIEW of the automatic discounts configured in Shopify Admin (e.g.
     "10% off for 2 people" / "15% off for 3+" on the All Public Workshops
     collection). The widget never applies a discount itself — checkout does.
     Keep cfg.groupOffer.tiers in step with those discounts, or the receipt
     will promise a number checkout does not honour.                       */

  // Seats the discount counts: every line this booking will add to the cart,
  // companions included — Shopify's minimum is a quantity across the
  // collection, and all of these lines are variants of this one product.
  Widget.prototype.offerSeats = function () {
    var n = 0;
    this.lines().forEach(function (ln) { n += ln.quantity; });
    return n;
  };

  Widget.prototype.offerSubtotal = function () {
    var n = 0;
    this.lines().forEach(function (ln) { if (ln.variant) n += ln.variant.price * ln.quantity; });
    return n;
  };

  // Ascending by seats, so the best tier at or below the count wins.
  Widget.prototype.offerTiers = function () {
    var go = this.cfg.groupOffer;
    if (!go || !go.tiers || !go.tiers.length) return [];
    return go.tiers.slice().sort(function (a, b) { return a.qty - b.qty; });
  };

  Widget.prototype.offerTierFor = function (seats) {
    var hit = null;
    this.offerTiers().forEach(function (t) { if (seats >= t.qty) hit = t; });
    return hit;
  };

  Widget.prototype.offerNextTier = function (seats) {
    var next = null;
    this.offerTiers().forEach(function (t) { if (!next && seats < t.qty) next = t; });
    return next;
  };

  Widget.prototype.offerCut = function (subtotal, tier) {
    return tier ? Math.round(subtotal * tier.percent / 100) : 0;
  };

  /* ---------- loading availability ------------------------------------ */

  // Drop anything BookThatApp will not sell. Called once the product feed
  // lands; re-renders if the list is already on screen.
  Widget.prototype.applyIgnored = function (ignored) {
    var n = 0, self = this;
    if (!ignored) return;
    var keep = function (v) { if (ignored[String(v.id)]) { n++; return false; } return true; };
    this.projects = this.projects.filter(keep);
    if (this.companionVariant && ignored[String(this.companionVariant.id)]) { this.companionVariant = null; n++; }
    if (this.decideVariant && ignored[String(this.decideVariant.id)]) { this.decideVariant = null; n++; }
    if (!n) return;
    this.ignoredTitles = (this.cfg.variants || [])
      .filter(function (v) { return ignored[String(v.id)]; })
      .map(function (v) { return v.title; });
    // The page's own project grid and strip are rendered by Liquid, which
    // cannot see this flag, so tell them.
    try {
      document.dispatchEvent(new CustomEvent('mas:booking:hidden-projects', {
        detail: { titles: this.ignoredTitles }
      }));
    } catch (e) { /* older browsers: the widget is still correct */ }
    if (this.state.selectedSlot) { this.renderProjects(); this.renderSummary(); this.sync(); }
    track('projects_hidden', { count: n });
  };

  Widget.prototype.init = function () {
    var self = this;
    var now = partsIn(new Date(), this.tz);
    this.state.view = { year: now.year, month: now.month };
    this.setBusy(true);
    // In parallel with the calendar; failure is non-fatal and simply leaves
    // every variant listed, which is how this behaved before.
    this.client.ignoredVariants(this.cfg.productId)
      .then(function (ignored) { self.applyIgnored(ignored); })
      .catch(function (e) { if (window.console) console.warn('[MAS booking] variant visibility unavailable', e); });
    this.loadMonth(now.year, now.month).then(function (entry) {
      // Open on a month that actually has open days, not merely one that has
      // sessions — see openOnAvailableMonth.
      return self.openOnAvailableMonth(entry);
    }).catch(function (e) {
      self.setError(self.cfg.text.loadError);
      if (window.console) console.error('[MAS booking] frames failed', e);
    }).then(function () {
      self.setBusy(false);
      self.renderCalendar();
      self.booted = true;
      track('calendar_visible', {
        opened_month: self.monthKey(self.state.view.year, self.state.view.month),
        months_skipped: self.state.reachAhead || 0
      });
    });
  };

  // How far the widget may carry a shopper on its own before giving up and
  // showing what it has. monthCache makes revisits free, so MAX_HOPS bounds
  // *new* frames requests, not months examined.
  var OPEN_MAX_HOPS = 6;
  var OPEN_MAX_AHEAD = 12;
  var OPEN_BUDGET_MS = 4000;

  Widget.prototype.monthKey = function (y, m) { return y + '-' + pad2(m); };

  Widget.prototype.loadMonth = function (year, month) {
    var self = this, key = this.monthKey(year, month);
    if (this.monthCache[key]) return Promise.resolve(this.monthCache[key]);
    var b = monthBoundsISO(year, month, this.tz);
    return this.client.frames(this.cfg.productId, b.start, b.finish).then(function (data) {
      var entry = {
        frames: data.frames || [],
        nextOccurrence: data.next_occurrence || null,
        firstOccurrence: data.first_occurrence || null,
        lastOccurrence: data.last_occurrence || null
      };
      self.monthCache[key] = entry;
      if (entry.firstOccurrence && !self.state.firstOccurrence) self.state.firstOccurrence = entry.firstOccurrence;
      if (entry.lastOccurrence) self.state.lastOccurrence = entry.lastOccurrence;
      var nowMs = Date.now();
      entry.frames.forEach(function (f) {
        (f.timeslots || []).forEach(function (s) {
          if (new Date(s.start).getTime() <= nowMs) return;          // already started
          var d = ymd(partsIn(new Date(s.start), self.tz));           // date in LOCATION tz, not UTC
          (self.slotsByDate[d] = self.slotsByDate[d] || []).push(s);
        });
      });
      Object.keys(self.slotsByDate).forEach(function (d) {
        self.slotsByDate[d].sort(function (a, b) { return new Date(a.start) - new Date(b.start); });
        // de-dupe (a month window can overlap at the edges)
        var seen = {}; self.slotsByDate[d] = self.slotsByDate[d].filter(function (s) { if (seen[s.id]) return false; seen[s.id] = 1; return true; });
      });
      return entry;
    });
  };

  // { had: any session that day, seats: open seats across its sessions }
  Widget.prototype.daySeats = function (key) {
    var slots = this.slotsByDate[key] || [], sum = 0;
    slots.forEach(function (s) { var st = slotSeats(s); if (st.open) sum += st.pool; });
    return { had: slots.length > 0, seats: sum };
  };

  Widget.prototype.datesInView = function () {
    var v = this.state.view, prefix = this.monthKey(v.year, v.month) + '-';
    var self = this;
    return Object.keys(this.slotsByDate).filter(function (d) {
      return d.indexOf(prefix) === 0 && self.daySeats(d).seats > 0;
    }).sort();
  };

  Widget.prototype.monthIndex = function (p) {
    var now = partsIn(new Date(), this.tz);
    return (p.year - now.year) * 12 + (p.month - now.month);
  };
  Widget.prototype.viewIndex = function () { return this.monthIndex(this.state.view); };
  // What the merchant configured, widened to include any month we opened on.
  // Replaces mutating cfg.monthsAhead, which used to leak into the rest of the session.
  Widget.prototype.reachAhead = function () {
    return Math.max(this.cfg.monthsAhead || 4, this.state.reachAhead || 0);
  };

  // Walk forward until the view lands on a month that actually has open days.
  //
  // One invariant does the real work: every hop must land on a month STRICTLY
  // LATER than the one just examined. That alone stops a sold-out current month
  // re-opening itself, keeps us out of the past, and guarantees termination —
  // the caps below are only a ceiling on cost against a pathological response.
  //
  // Never rejects. Whatever happens the caller can clear the spinner and render
  // a calendar; a mid-hunt failure falls back to the last month that did load.
  Widget.prototype.openOnAvailableMonth = function (entry) {
    var self = this;
    var deadline = Date.now() + OPEN_BUDGET_MS;
    var hops = 0;
    var lastGood = { year: this.state.view.year, month: this.state.view.month };

    function step(cur) {
      if (self.datesInView().length) return Promise.resolve();   // arrived
      if (!cur) return Promise.resolve();
      if (hops >= OPEN_MAX_HOPS || Date.now() > deadline) return Promise.resolve();

      var here = self.viewIndex();
      // first_occurrence is GLOBAL, so on the first hop it can skip many empty
      // months at once; next_occurrence is scoped to the window just requested,
      // so on later hops it is the only field that moves. Take the EARLIEST
      // candidate beyond `here` — jumping to a far first_occurrence when a
      // nearer next_occurrence exists would skip a month that has sessions.
      var best = null;
      [cur.firstOccurrence, cur.nextOccurrence, self.state.firstOccurrence].forEach(function (iso) {
        if (!iso) return;
        var p = partsIn(new Date(iso), self.tz);
        var idx = self.monthIndex(p);
        if (idx <= here || idx > OPEN_MAX_AHEAD) return;
        if (!best || idx < best.idx) best = { idx: idx, p: p };
      });
      if (!best) return Promise.resolve();      // nothing further to follow

      hops++;
      self.state.view = { year: best.p.year, month: best.p.month };
      self.state.reachAhead = Math.max(self.state.reachAhead || 0, best.idx);
      return self.loadMonth(best.p.year, best.p.month).then(function (next) {
        lastGood = { year: best.p.year, month: best.p.month };
        return step(next);
      });
    }

    return step(entry).catch(function (e) {
      self.state.view = lastGood;
      self.setError(self.cfg.text.loadError);
      if (window.console) console.error('[MAS booking] month hop failed', e);
    });
  };

  Widget.prototype.shiftMonth = function (delta) {
    var v = this.state.view, m = v.month + delta, y = v.year;
    if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
    var now = partsIn(new Date(), this.tz);
    if (y < now.year || (y === now.year && m < now.month)) return;
    var maxAhead = this.reachAhead();
    var aheadIdx = (y - now.year) * 12 + (m - now.month);
    if (aheadIdx > maxAhead) return;
    var self = this;
    this.state.view = { year: y, month: m };
    this.state.selectedDate = null; this.state.selectedSlot = null;
    this.setBusy(true);
    this.loadMonth(y, m).catch(function (e) {
      self.setError(self.cfg.text.loadError);
    }).then(function () { self.setBusy(false); self.renderCalendar(); });
  };

  Widget.prototype.jumpToNextAvailable = function () {
    var v = this.state.view, entry = this.monthCache[this.monthKey(v.year, v.month)];
    if (!entry || !entry.nextOccurrence) return;
    var self = this;
    this.state.selectedDate = null; this.state.selectedSlot = null;
    this.setBusy(true);
    // Shares init's resolver, so the button keeps hopping past fully-booked
    // months instead of landing on one and asking for another click.
    this.openOnAvailableMonth(entry)
      .then(function () { self.setBusy(false); self.renderCalendar(); });
  };

  /* ---------- 1. date & time ------------------------------------------ */

  Widget.prototype.renderCalendar = function () {
    var v = this.state.view, self = this, text = this.cfg.text;
    this.ui.monthLabel.textContent = MONTHS[v.month - 1] + ' ' + v.year;
    var cal = this.ui.calendar; cal.innerHTML = '';

    WEEKDAYS.forEach(function (d) { cal.appendChild(el('div', 'masbk__dow', d)); });

    var firstDow = new Date(Date.UTC(v.year, v.month - 1, 1)).getUTCDay();
    var daysInMonth = new Date(Date.UTC(v.year, v.month, 0)).getUTCDate();
    var todayKey = ymd(partsIn(new Date(), this.tz));
    var openDates = this.datesInView();

    for (var i = 0; i < firstDow; i++) cal.appendChild(el('div', 'masbk__day masbk__day--pad'));
    for (var d = 1; d <= daysInMonth; d++) {
      var key = this.monthKey(v.year, v.month) + '-' + pad2(d);
      var info = this.daySeats(key);
      var open = openDates.indexOf(key) >= 0;
      var btn = el('button', 'masbk__day');
      btn.type = 'button';
      btn.setAttribute('data-date', key);
      btn.appendChild(el('span', 'masbk__day-n', String(d)));
      if (key === todayKey) btn.classList.add('is-today');
      if (open) {
        var seatsText = fill(text.seats, 'n', info.seats);
        if (self.showCount(info.seats)) {
          var lab = el('span', 'masbk__day-seats', fill(text.daySeats, 'n', info.seats));
          if (self.cfg.lowSeatThreshold && info.seats <= self.cfg.lowSeatThreshold) lab.classList.add('is-low');
          btn.appendChild(lab);
        } else btn.appendChild(el('span', 'masbk__day-dot'));
        btn.setAttribute('aria-label', niceDate(this.slotsByDate[key][0].start, this.tz) + (self.cfg.showSeats ? ', ' + seatsText : ''));
        btn.addEventListener('click', this.onPickDate.bind(this, key));
      } else {
        btn.disabled = true; btn.classList.add('is-off');
        if (info.had) { btn.classList.add('is-full'); btn.appendChild(el('span', 'masbk__day-seats', text.dayFull)); }
      }
      if (key === this.state.selectedDate) { btn.classList.add('is-selected'); btn.setAttribute('aria-pressed', 'true'); }
      cal.appendChild(btn);
    }

    var now = partsIn(new Date(), this.tz);
    var aheadIdx = (v.year - now.year) * 12 + (v.month - now.month);
    this.ui.prev.disabled = aheadIdx <= 0;
    this.ui.next.disabled = aheadIdx >= this.reachAhead();

    var entry = this.monthCache[this.monthKey(v.year, v.month)];
    var showJump = !openDates.length && entry && entry.nextOccurrence && new Date(entry.nextOccurrence) > new Date(Date.UTC(v.year, v.month, 1));
    this.ui.jump.hidden = !showJump;
    if (showJump) {
      var p = partsIn(new Date(entry.nextOccurrence), this.tz);
      this.ui.jump.textContent = fill(text.jump, 'month', MONTHS[p.month - 1] + ' ' + p.year);
    }
    this.ui.empty.hidden = !!openDates.length || !!showJump;

    this.renderSlots();
    this.sync();
  };

  Widget.prototype.onPickDate = function (key) {
    if (this.state.selectedDate === key) return;
    this.state.selectedDate = key;
    this.state.selectedSlot = null;
    this.setError(null);
    qsa(this.ui.calendar, '.masbk__day').forEach(function (b) {
      var sel = b.getAttribute('data-date') === key;
      b.classList.toggle('is-selected', sel);
      if (sel) b.setAttribute('aria-pressed', 'true'); else b.removeAttribute('aria-pressed');
    });
    this.renderSlots();
    this.sync();
    track('date_selected', { booking_date: key });
  };

  Widget.prototype.renderSlots = function () {
    var box = this.ui.slots; box.innerHTML = '';
    var date = this.state.selectedDate;
    var self = this;
    this.ui.pickDate.hidden = !!date || !this.datesInView().length;
    if (!date) return;
    var slots = (this.slotsByDate[date] || []);
    this.ui.slotsTitle.textContent = niceDate(slots.length ? slots[0].start : date + 'T12:00:00Z', this.tz);
    slots.forEach(function (s) {
      var seats = slotSeats(s);
      var b = el('button', 'masbk__slot'); b.type = 'button';
      var label = niceTime(s.start, self.tz);
      if (self.cfg.showTimeRange) label += ' – ' + niceTime(s.finish, self.tz);
      b.appendChild(el('span', 'masbk__slot-time', label));
      var seatsText;
      if (!seats.open) seatsText = self.cfg.text.soldOut;
      else if (self.showCount(seats.pool)) seatsText = seats.pool === 1 ? self.cfg.text.oneSeat : fill(self.cfg.text.seats, 'n', seats.pool);
      else seatsText = self.cfg.text.available;
      var st = el('span', 'masbk__slot-seats', seatsText);
      if (seats.open && self.cfg.lowSeatThreshold && seats.pool <= self.cfg.lowSeatThreshold) st.classList.add('is-low');
      b.appendChild(st);
      if (!seats.open) { b.disabled = true; b.classList.add('is-off'); }
      else b.addEventListener('click', function () { self.onPickSlot(s); });
      if (self.state.selectedSlot && self.state.selectedSlot.id === s.id) { b.classList.add('is-selected'); b.setAttribute('aria-pressed', 'true'); }
      box.appendChild(b);
    });
  };

  Widget.prototype.onPickSlot = function (slot) {
    var same = this.state.selectedSlot && this.state.selectedSlot.id === slot.id;
    this.state.selectedSlot = slot;
    this.setError(null);
    this.clampToSlot();
    this.renderSlots();
    this.renderBelowSession();
    this.advance();
    this.sync();
    if (!same) track('session_selected', { booking_start: slot.start });
  };

  // Keep earlier answers when they still fit the newly selected session,
  // clear the ones that no longer do (they are asked again).
  Widget.prototype.clampToSlot = function () {
    var s = this.state, slot = s.selectedSlot, self = this;
    if (!slot) return;
    if (s.people != null && s.people > this.peopleMax()) { s.people = null; s.peopleMore = false; }
    if (s.people == null) { s.together = null; s.companions = null; }
    if (s.together === true && s.companions != null && s.companions > this.companionMax()) s.companions = null;
    if (s.mode === 'later' && !this.decideFor(slot)) s.mode = null;
    var seats = slotSeats(slot);
    Object.keys(s.qty).forEach(function (id) {
      var m = self.variantMax(self.variantById(id), seats);
      if (s.qty[id] > m) s.qty[id] = m;
      if (s.qty[id] <= 0) delete s.qty[id];
    });
    if (this.simple) {
      if (this.chosen() > seats.pool) s.qty = {};
      if (!this.decideFor(slot)) s.decideQty = 0;
      else s.decideQty = Math.max(0, Math.min(s.decideQty || 0, this.variantMax(this.decideVariant, seats), seats.pool));
      if (!this.companionFor(slot)) s.companions = 0;
      else s.companions = Math.max(0, Math.min(s.companions || 0, seats.pool - this.makers()));
      return;
    }
    if (this.chosen() > this.makers()) s.qty = {};
  };

  Widget.prototype.renderBelowSession = function () {
    this.renderPeople();
    this.renderTogether();
    this.renderMode();
    this.renderDecideRow();
    this.renderProjects();
    this.renderSummary();
  };

  /* ---------- 2. who's coming ----------------------------------------- */

  Widget.prototype.chip = function (label, selected, onClick) {
    var b = el('button', 'masbk__chip', label); b.type = 'button';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', selected ? 'true' : 'false');
    if (selected) b.classList.add('is-selected');
    b.addEventListener('click', onClick);
    return b;
  };

  Widget.prototype.renderPeople = function () {
    var s = this.state, slot = s.selectedSlot, self = this;
    if (!slot || !this.ui.peopleChips) return;
    var max = this.peopleMax();
    var box = this.ui.peopleChips; box.innerHTML = '';
    var useMore = max > CHIP_LIMIT + 1;
    var chips = useMore ? CHIP_LIMIT : max;
    for (var n = 1; n <= chips; n++) box.appendChild(this.chip(String(n), s.people === n, this.onPickPeople.bind(this, n, false)));
    var showStep = false;
    if (useMore) {
      var bigSel = s.people != null && s.people > CHIP_LIMIT;
      showStep = bigSel || s.peopleMore;
      box.appendChild(this.chip(this.cfg.text.peopleMore, showStep, function () {
        self.state.peopleMore = true;
        self.onPickPeople(Math.max(CHIP_LIMIT + 1, self.state.people || 0), true);
      }));
    }
    this.ui.peopleStep.hidden = !showStep;
    if (showStep) {
      var val = Math.max(CHIP_LIMIT + 1, s.people || 0);
      this.ui.peopleStepVal.textContent = String(val);
      this.ui.peopleMinus.disabled = val <= CHIP_LIMIT + 1;
      this.ui.peoplePlus.disabled = val >= max;
    }
    // The stepper never folds the step by itself (people may still be counting);
    // a Continue button does that once nothing else is left to answer.
    this.ui.peopleContinue.hidden = !(showStep && this.peopleDone());
  };

  Widget.prototype.onPickPeople = function (n, fromStepper) {
    var s = this.state, max = this.peopleMax();
    n = Math.max(1, Math.min(max, n));
    if (!fromStepper) s.peopleMore = false;
    var changed = s.people !== n;
    s.people = n;
    if (s.together === true && s.companions != null && s.companions > this.companionMax()) s.companions = null;
    if (this.chosen() > this.makers()) s.qty = {};
    this.setError(null);
    this.renderBelowSession();
    if (!fromStepper && this.peopleDone()) this.advance();
    this.sync();
    if (changed) track('people_selected', { people: n });
  };

  Widget.prototype.renderTogether = function () {
    var s = this.state, slot = s.selectedSlot, self = this, text = this.cfg.text;
    if (!slot || !this.ui.togetherHint) return;
    var comp = this.companionFor(slot);
    this.ui.togetherHint.textContent = comp ? fill(text.togetherHint, 'price', money(comp.price, this.cfg.moneyFormat)) : '';
    qsa(this.root, '[data-mas-together]').forEach(function (b) {
      var sel = s.together === (b.getAttribute('data-mas-together') === 'yes');
      b.classList.toggle('is-selected', sel);
      b.setAttribute('aria-checked', sel ? 'true' : 'false');
    });
    var box = this.ui.companionChips; box.innerHTML = '';
    if (this.togetherApplies() && s.together === true && s.people >= 3) {
      var maxC = this.companionMax();
      for (var c = 1; c <= maxC; c++) box.appendChild(this.chip(String(c), s.companions === c, this.onPickCompanions.bind(this, c)));
    }
    var split = this.ui.peopleSplit;
    var show = this.peopleDone() && this.companions() > 0;
    split.hidden = !show;
    if (show) split.textContent = this.whoText();
  };

  Widget.prototype.makersText = function () {
    var m = this.makers();
    return m === 1 ? this.cfg.text.makerOne : fill(this.cfg.text.makerMany, 'n', m);
  };
  Widget.prototype.companionsText = function () {
    var c = this.companions();
    return c === 1 ? this.cfg.text.companionOne : fill(this.cfg.text.companionMany, 'n', c);
  };
  Widget.prototype.peopleText = function () {
    var n = this.peopleCount();
    return n === 1 ? this.cfg.text.onePerson : fill(this.cfg.text.people, 'n', n);
  };
  // "2 people (1 maker + 1 companion)" — the total first, then how it splits,
  // so a folded bar never reads as if companions come on top of the count.
  Widget.prototype.whoText = function () {
    var who = this.peopleText();
    if (this.companions() > 0) who += ' (' + this.makersText() + ' + ' + this.companionsText() + ')';
    return who;
  };

  Widget.prototype.onPickTogether = function (yes) {
    var s = this.state, same = s.together === yes;
    s.together = yes;
    if (this.chosen() > this.makers()) s.qty = {};
    this.setError(null);
    this.renderBelowSession();
    if (this.peopleDone()) this.advance();
    this.sync();
    if (!same) track('together_selected', { together: yes, companions: this.companions() });
  };

  Widget.prototype.onPickCompanions = function (c) {
    var s = this.state, same = s.companions === c;
    s.companions = c;
    if (this.chosen() > this.makers()) s.qty = {};
    this.setError(null);
    this.renderBelowSession();
    if (this.peopleDone()) this.advance();
    this.sync();
    if (!same) track('together_selected', { together: true, companions: this.companions() });
  };

  /* ---------- 3. projects --------------------------------------------- */

  Widget.prototype.renderMode = function () {
    var s = this.state, d = this.decideFor(s.selectedSlot), text = this.cfg.text;
    qsa(this.root, '[data-mas-mode]').forEach(function (b) {
      var sel = s.mode === b.getAttribute('data-mas-mode');
      b.setAttribute('aria-checked', sel ? 'true' : 'false');
      // The card is what reads as picked, not the button inside it.
      var card = b.parentNode;
      if (card && card.classList) card.classList.toggle('is-selected', sel);
    });
    // No price on the answers themselves — what the held seat costs, and what
    // it covers, is the first thing the revealed hint says.
    var price = d ? money(d.price, this.cfg.moneyFormat) : '';
    if (this.ui.modeLaterHint) this.ui.modeLaterHint.textContent = d ? fill(text.modeLaterHint, 'price', price) : '';
  };

  Widget.prototype.onPickMode = function (mode) {
    var s = this.state, same = s.mode === mode;
    s.mode = mode;
    // Choosing to decide at the studio is itself a booking for one; the
    // stepper is for changing that, not for starting from nothing.
    if (mode === 'later' && this.simple && !s.decideQty && this.decideMax() > 0) s.decideQty = 1;
    this.setError(null);
    this.renderBelowSession();
    if (this.complete()) this.advance();
    this.sync();
    if (!same) track('project_mode_selected', { mode: mode });
  };

  Widget.prototype.renderProjects = function () {
    var slot = this.state.selectedSlot, self = this;
    var box = this.ui.projects; box.innerHTML = '';
    this.ui.more.hidden = true;
    if (!slot) return;
    var seats = slotSeats(slot);
    var shown = this.cfg.projectsShown || 4, extra = 0;
    this.projects.forEach(function (v, i) {
      var max = self.variantMax(v, seats);
      var cur = self.state.qty[v.id] || 0;
      var row = el('div', 'masbk__project');
      row.setAttribute('data-variant', v.id);
      // Everything stays in the grid; the overflow is clipped and faded by
      // applyProjectsCollapse, so shoppers can see there is more below.
      if (i >= shown) extra++;
      if (v.image) {
        var img = el('img', 'masbk__project-img'); img.src = v.image; img.alt = v.title; img.loading = 'lazy'; img.width = 300; img.height = 300;   // square; CSS sizes it to the column
        // Two per row: ~145px in the product-page sidebar, ~275px in the modal, ~45vw on phones.
        // sizes is a static hint and the widget has no single width, so it leans high to stay sharp on retina.
        if (v.imageSrcset) { img.srcset = v.imageSrcset; img.sizes = '(max-width: 640px) 45vw, 300px'; }
        row.appendChild(img);
      } else row.appendChild(el('div', 'masbk__project-img masbk__project-img--blank'));
      var info = el('div', 'masbk__project-info');
      info.appendChild(el('div', 'masbk__project-title', v.title));
      var price = el('div', 'masbk__project-price', money(v.price, self.cfg.moneyFormat));
      if (v.compareAt && v.compareAt > v.price) {
        price.appendChild(el('s', 'masbk__project-compare', money(v.compareAt, self.cfg.moneyFormat)));
      }
      info.appendChild(price);
      if (max <= 0) info.appendChild(el('div', 'masbk__project-note', self.cfg.text.projectUnavailable));
      row.appendChild(info);

      var ctr = el('div', 'masbk__qty');
      var minus = el('button', 'masbk__qty-btn', '−'); minus.type = 'button'; minus.setAttribute('aria-label', 'Remove one ' + v.title); minus.setAttribute('data-qty-minus', '');
      var out = el('output', 'masbk__qty-val', String(cur)); out.setAttribute('aria-live', 'polite');
      var plus = el('button', 'masbk__qty-btn', '+'); plus.type = 'button'; plus.setAttribute('aria-label', 'Add one ' + v.title); plus.setAttribute('data-qty-plus', '');
      minus.addEventListener('click', function () { self.changeQty(v.id, -1); });
      plus.addEventListener('click', function () { self.changeQty(v.id, 1); });
      if (max <= 0) row.classList.add('is-off');
      if (cur > 0) row.classList.add('is-picked');
      ctr.appendChild(minus); ctr.appendChild(out); ctr.appendChild(plus);
      row.appendChild(ctr);
      box.appendChild(row);
    });
    if (extra > 0) {
      this.ui.more.hidden = false;
      this.ui.more.textContent = fill(this.cfg.text.moreProjects, 'n', extra);
    }
    this.applyProjectsCollapse();
    this.renderCompanionRow();
    this.renderProgress();
  };

  // Clip the grid just below the first `projectsShown` cards, leaving the next
  // row half-visible under the gradient. Measured rather than a fixed height,
  // because card height depends on column width and title wrapping.
  Widget.prototype.applyProjectsCollapse = function () {
    var box = this.ui.projects, shown = this.cfg.projectsShown || 4;
    var tiles = qsa(box, '.masbk__project');
    var self = this;
    // A project picked from below the fold would otherwise be out of sight.
    var pickedBelow = tiles.slice(shown).some(function (row) {
      var v = self.variantById(row.getAttribute('data-variant'));
      return v && (self.state.qty[v.id] || 0) > 0;
    });
    if (pickedBelow) this.state.showAllProjects = true;

    var collapsed = !this.state.showAllProjects && tiles.length > shown;
    box.classList.toggle('is-collapsed', collapsed);
    this.ui.more.hidden = !collapsed;
    if (!collapsed) {
      box.style.maxHeight = '';
      if (this._projObs) { this._projObs.disconnect(); this._projObs = null; }
      return;
    }

    var measure = function () {
      if (!box.classList.contains('is-collapsed')) return;
      var last = tiles[shown - 1];
      if (!last) return;
      var h = last.getBoundingClientRect().bottom - box.getBoundingClientRect().top;
      if (h > 0) box.style.maxHeight = Math.round(h + 54) + 'px';   // 54px of the next row peeks through
    };
    measure();
    // Card height is not final until the thumbnails have laid out and titles
    // have wrapped, and measuring too early clips mid-card. Watch the grid
    // instead of guessing when that has happened.
    if (this._projObs) { this._projObs.disconnect(); this._projObs = null; }
    if (window.ResizeObserver) {
      this._projObs = new ResizeObserver(function () { measure(); });
      this._projObs.observe(box);
    } else {
      if (window.requestAnimationFrame) requestAnimationFrame(measure);
      qsa(box, '.masbk__project-img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', measure, { once: true });
      });
      setTimeout(measure, 400);
    }
  };

  // Simple flow: the companion add-on sits under the project list as a row
  // with its own stepper (a companion joins a maker on the same project).
  Widget.prototype.renderCompanionRow = function () {
    var box = this.ui.companionRow; if (!box) return;
    var slot = this.state.selectedSlot, c = slot ? this.companionFor(slot) : null, self = this, text = this.cfg.text;
    box.innerHTML = '';
    box.hidden = !c;
    if (!c) return;
    var info = el('div', 'masbk__project-info');
    info.appendChild(el('div', 'masbk__project-title', c.title));
    info.appendChild(el('div', 'masbk__project-price', money(c.price, this.cfg.moneyFormat)));
    info.appendChild(el('div', 'masbk__companion-hint', fill(text.togetherHint, 'price', money(c.price, this.cfg.moneyFormat))));
    var ctr = el('div', 'masbk__qty');
    var minus = el('button', 'masbk__qty-btn', '−'); minus.type = 'button'; minus.setAttribute('aria-label', 'Remove one ' + c.title); minus.setAttribute('data-comp-minus', '');
    var out = el('output', 'masbk__qty-val', String(this.companions())); out.setAttribute('aria-live', 'polite'); out.setAttribute('data-comp-val', '');
    var plus = el('button', 'masbk__qty-btn', '+'); plus.type = 'button'; plus.setAttribute('aria-label', 'Add one ' + c.title); plus.setAttribute('data-comp-plus', '');
    minus.addEventListener('click', function () { self.changeCompanions(-1); });
    plus.addEventListener('click', function () { self.changeCompanions(1); });
    ctr.appendChild(minus); ctr.appendChild(out); ctr.appendChild(plus);
    box.appendChild(info); box.appendChild(ctr);
    box.classList.toggle('is-picked', this.companions() > 0);
  };

  // Simple flow: holding seats to decide at the studio needs a count, because
  // there are no project cards to add up. Same row, same stepper.
  Widget.prototype.renderDecideRow = function () {
    var box = this.ui.decideRow; if (!box) return;
    var slot = this.state.selectedSlot, d = slot ? this.decideFor(slot) : null, self = this, text = this.cfg.text;
    box.innerHTML = '';
    if (!d) return;
    var info = el('div', 'masbk__project-info');
    info.appendChild(el('div', 'masbk__project-title', d.title));
    info.appendChild(el('div', 'masbk__project-price', money(d.price, this.cfg.moneyFormat)));
    var ctr = el('div', 'masbk__qty');
    var minus = el('button', 'masbk__qty-btn', '\u2212'); minus.type = 'button'; minus.setAttribute('aria-label', 'One seat fewer'); minus.setAttribute('data-decide-minus', '');
    var out = el('output', 'masbk__qty-val', String(this.decideSeats())); out.setAttribute('aria-live', 'polite'); out.setAttribute('data-decide-val', '');
    var plus = el('button', 'masbk__qty-btn', '+'); plus.type = 'button'; plus.setAttribute('aria-label', 'One seat more'); plus.setAttribute('data-decide-plus', '');
    minus.addEventListener('click', function () { self.changeDecideSeats(-1); });
    plus.addEventListener('click', function () { self.changeDecideSeats(1); });
    ctr.appendChild(minus); ctr.appendChild(out); ctr.appendChild(plus);
    box.appendChild(info); box.appendChild(ctr);
    box.classList.toggle('is-picked', this.decideSeats() > 0);
  };

  Widget.prototype.changeDecideSeats = function (delta) {
    var s = this.state; if (!this.simple || !s.selectedSlot) return;
    var next = Math.max(0, Math.min(this.decideMax(), this.decideSeats() + delta));
    if (next === this.decideSeats()) return;
    s.decideQty = next;
    this.setError(null);
    var box = this.ui.decideRow;
    if (box) { var v = qs(box, '[data-decide-val]'); if (v) v.textContent = String(next); box.classList.toggle('is-picked', next > 0); }
    this.renderProgress();
    this.renderSummary();
    this.sync();
    track('decide_seats_selected', { seats: next });
  };

  Widget.prototype.changeCompanions = function (delta) {
    var s = this.state; if (!this.simple || !s.selectedSlot) return;
    var next = Math.max(0, Math.min(this.companionMax(), (s.companions || 0) + delta));
    if (next === (s.companions || 0)) return;
    s.companions = next;
    this.setError(null);
    var box = this.ui.companionRow;
    if (box) { var v = qs(box, '[data-comp-val]'); if (v) v.textContent = String(next); box.classList.toggle('is-picked', next > 0); }
    this.renderProgress();
    this.renderSummary();
    this.sync();
    track('companions_selected', { companions: next });
  };

  // The upsell line under the project list: what one (or a few) more seats is
  // worth in money, not just a percentage. Recomputed on every quantity change.
  Widget.prototype.renderOfferNudge = function () {
    var box = this.ui.offerNudge;
    if (!box) return;
    var tiers = this.offerTiers();
    if (!tiers.length) { box.hidden = true; return; }

    var text = this.cfg.text, fmt = this.cfg.moneyFormat;
    var seats = this.offerSeats(), sub = this.offerSubtotal();
    var tier = this.offerTierFor(seats), next = this.offerNextTier(seats);
    var msg = '', state = 'is-idle', amount = '';

    if (seats < 1) {
      // Every tier, so the whole offer is visible before a single seat is
      // chosen. From the first seat on, this bar stops describing the offer
      // and starts costing it out for THIS booking instead.
      var rungs = tiers.map(function (t, i) {
        var qty = t.qty + (i === tiers.length - 1 ? '+' : '');
        return fill(fill(text.offerTier, 'pct', t.percent), 'n', qty);
      }).join(text.offerTierSep);
      msg = fill(text.offerLadder, 'tiers', rungs);
    } else if (next) {
      // What the shopper gains by reaching the next tier: the discount on the
      // BIGGER booking, less whatever they already qualify for. The extra seats
      // are priced at the average seat so far — the nudge fires before we know
      // which project they would add.
      var need = next.qty - seats;
      var perSeat = Math.round(sub / seats);
      var after = this.offerCut(sub + perSeat * need, next);
      var already = this.offerCut(sub, tier);
      var gain = Math.max(0, after - already);
      amount = money(gain, fmt);
      if (tier) {
        msg = fill(fill(fill(fill(need === 1 ? text.offerUpgrade : text.offerUpgradeMany, 'k', need), 'pct', tier.percent), 'next', next.percent), 'save', amount);
      } else {
        msg = fill(fill(fill(need === 1 ? text.offerAddOne : text.offerAddMany, 'k', need), 'pct', next.percent), 'save', amount);
      }
      state = 'is-close';
    } else if (tier) {
      amount = money(this.offerCut(sub, tier), fmt);
      msg = fill(fill(text.offerApplied, 'pct', tier.percent), 'save', amount);
      state = 'is-won';
    }

    // The money is the point, so it is a <b> inside the sentence rather than
    // just more text. Built from nodes, never innerHTML.
    var out = this.ui.offerNudgeText || box;
    out.textContent = '';
    var cut = amount && msg.indexOf(amount) >= 0 ? msg.indexOf(amount) : -1;
    if (cut < 0) {
      out.textContent = msg;
    } else {
      out.appendChild(document.createTextNode(msg.slice(0, cut)));
      out.appendChild(el('b', 'masbk__nudge-amt', amount));
      out.appendChild(document.createTextNode(msg.slice(cut + amount.length)));
    }
    // Real scarcity only. Same threshold the calendar uses, and the same number
    // it shows, so the widget never invents pressure — if the session is not
    // actually filling up, this line does not appear at all. Nothing is held
    // until checkout, so the pool is still the honest figure here.
    var sub = this.ui.offerNudgeSub;
    if (sub) {
      var room = this.state.selectedSlot ? slotSeats(this.state.selectedSlot) : null;
      var scarce = state === 'is-close' && room && room.pool > 0 && this.showCount(room.pool);
      sub.textContent = scarce
        ? (room.pool === 1 ? text.offerOneSeatLeft : fill(text.offerSeatsLeft, 'n', room.pool))
        : '';
      sub.hidden = !scarce;
    }

    // sync() re-renders this bar on every step change, so rebuilding the class
    // list has to carry is-swap across — otherwise the second render strips the
    // animation off the first one and the line changes without moving.
    box.className = 'masbk__nudge ' + state + (box.classList.contains('is-swap') ? ' is-swap' : '');
    box.hidden = !msg;
    if (msg && this._nudgeMsg !== msg) {
      this._nudgeMsg = msg;
      if (this.booted) {
        box.classList.remove('is-swap');
        void box.offsetWidth;                 // restart the keyframes
        box.classList.add('is-swap');
      }
    }

    // Light up the banner's ladder: the tier earned, and the one a seat away.
    var tiers = this.ui.offerTiers || [];
    var onQty = tier ? tier.qty : 0, nextQty = next ? next.qty : 0;
    tiers.forEach(function (li) {
      var q = parseInt(li.getAttribute('data-mas-offer-tier'), 10);
      li.classList.toggle('is-on', !!onQty && q <= onQty);
      li.classList.toggle('is-next', q === nextQty && seats > 0);
    });
  };

  Widget.prototype.renderProgress = function () {
    var text = this.cfg.text, makers = this.makers(), k = this.chosen(), self = this;
    this.renderOfferNudge();
    var seats = this.state.selectedSlot ? slotSeats(this.state.selectedSlot) : null;
    if (this.simple) {
      // no target count: the projects define the group, the room caps it
      var total = k + this.companions(), pool = seats ? seats.pool : 0;
      this.ui.progressTitle.textContent = text.chooseProjects;
      this.ui.progress.textContent = k > 0 ? this.whoText() : '';
      this.ui.progress.classList.toggle('is-done', k > 0);
      qsa(this.ui.projects, '.masbk__project').forEach(function (row) {
        var v = self.variantById(row.getAttribute('data-variant'));
        var max = seats ? self.variantMax(v, seats) : 0;
        var cur = self.state.qty[v.id] || 0;
        qs(row, '[data-qty-plus]').disabled = max <= 0 || cur >= max || total >= pool;
        qs(row, '[data-qty-minus]').disabled = cur <= 0;
      });
      var box = this.ui.companionRow;
      if (box && !box.hidden) {
        var cm = this.companionMax(), cc = this.companions();
        var cp = qs(box, '[data-comp-plus]'), cmn = qs(box, '[data-comp-minus]');
        if (cp) cp.disabled = cc >= cm;
        if (cmn) cmn.disabled = cc <= 0;
      }
      var drow = this.ui.decideRow;
      if (drow && drow.firstChild) {
        var dmax = this.decideMax(), dcur = this.decideSeats();
        var dp = qs(drow, '[data-decide-plus]'), dm = qs(drow, '[data-decide-minus]');
        if (dp) dp.disabled = dcur >= dmax;
        if (dm) dm.disabled = dcur <= 0;
      }
      return;
    }
    this.ui.progressTitle.textContent = makers === 1 ? text.chooseOne : fill(text.chooseMany, 'n', makers);
    this.ui.progress.textContent = k >= makers && makers > 0 ? fill(text.chosenAll, 'n', makers) : fill(fill(text.chosen, 'k', k), 'n', makers);
    this.ui.progress.classList.toggle('is-done', k >= makers && makers > 0);
    var full = k >= makers;
    qsa(this.ui.projects, '.masbk__project').forEach(function (row) {
      var v = self.variantById(row.getAttribute('data-variant'));
      var max = seats ? self.variantMax(v, seats) : 0;
      var cur = self.state.qty[v.id] || 0;
      qs(row, '[data-qty-plus]').disabled = max <= 0 || cur >= max || full;
      qs(row, '[data-qty-minus]').disabled = cur <= 0;
    });
  };

  Widget.prototype.changeQty = function (variantId, delta) {
    var s = this.state, slot = s.selectedSlot; if (!slot) return;
    var v = this.variantById(variantId), max = this.variantMax(v, slotSeats(slot));
    var cur = s.qty[variantId] || 0;
    var next = Math.max(0, Math.min(max, cur + delta));
    if (this.simple) { if (delta > 0 && this.chosen() + this.companions() >= slotSeats(slot).pool) return; }   // room is full
    else if (delta > 0 && this.chosen() >= this.makers()) return;     // one project per maker
    if (next === cur) return;
    this.setError(null);
    if (next === 0) delete s.qty[variantId]; else s.qty[variantId] = next;
    var row = qs(this.ui.projects, '[data-variant="' + variantId + '"]');
    if (row) { qs(row, '.masbk__qty-val').textContent = String(next); row.classList.toggle('is-picked', next > 0); }
    this.renderProgress();
    this.renderSummary();
    this.sync();
  };

  /* ---------- 4. review & checkout ------------------------------------ */

  Widget.prototype.renderSummary = function () {
    var box = this.ui.receipt; box.innerHTML = '';
    this.ui.checkout.disabled = this.state.busy || !this.complete();
    if (!this.complete()) return;
    var fmt = this.cfg.moneyFormat, text = this.cfg.text;
    // Deliberately not a receipt any more. The date and time are already in
    // step 1's folded bar, and what was picked is on the cards right above —
    // repeating either is just noise between the shopper and the button. What
    // is NOT visible anywhere else is the money, so that is all this shows.
    var total = 0;
    this.lines().forEach(function (ln) { total += ln.variant.price * ln.quantity; });
    var tier = this.offerTierFor(this.offerSeats());
    var cut = this.offerCut(total, tier);

    var tot = el('div', 'masbk__total');
    tot.appendChild(el('span', 'masbk__total-label', text.total));
    var right = el('span', 'masbk__total-price');
    if (cut > 0) right.appendChild(el('s', 'masbk__total-was', money(total, fmt)));
    right.appendChild(el('span', 'masbk__total-now', money(total - cut, fmt)));
    tot.appendChild(right);
    box.appendChild(tot);

    // Shopify applies the discount at checkout, so say what they save rather
    // than implying the widget already took it off.
    if (cut > 0) box.appendChild(el('p', 'masbk__total-save', fill(text.offerSaved, 'save', money(cut, fmt))));
  };

  // What a folded step shows in its bar.
  Widget.prototype.stageSummary = function (name) {
    var s = this.state, slot = s.selectedSlot, text = this.cfg.text;
    if (name === 'date') return slot ? shortDate(slot.start, this.tz) + ' · ' + niceTime(slot.start, this.tz) : '';
    if (name === 'people') return this.peopleDone() ? this.whoText() : '';
    if (name === 'projects') {
      if (!this.complete()) return '';
      if (this.effectiveMode() === 'later') return text.modeLater;
      var list = this.lines().filter(function (ln) { return ln.variant.role === 'project'; })
        .map(function (ln) { return ln.variant.title + ' ' + fill(text.qty, 'n', ln.quantity); }).join(', ');
      if (this.simple && this.companions() > 0) list += ' · ' + this.companionsText();
      return list;
    }
    return '';
  };

  /* ---------- the stepper: fold, unfold, move on ----------------------- */

  // Shows / hides one block inside a step. Returns true when it was just revealed.
  Widget.prototype.setBlock = function (name, visible) {
    var node = this.blocks[name]; if (!node) return false;
    if (!!visible === !node.hidden) return false;
    node.hidden = !visible;
    if (visible && this.booted) {
      node.classList.remove('is-revealed');
      void node.offsetWidth;                 // restart the CSS animation
      node.classList.add('is-revealed');
    }
    return !!visible;
  };

  // Where every step stands right now.
  Widget.prototype.stageStatus = function () {
    var slot = this.state.selectedSlot, pd = this.peopleDone(), c = this.complete();
    if (this.simple) {
      return {
        date: { reachable: true, done: !!slot },
        projects: { reachable: !!slot, done: c }
      };
    }
    return {
      date: { reachable: true, done: !!slot },
      people: { reachable: !!slot, done: pd },
      projects: { reachable: pd, done: c }
    };
  };
  // The step to unfold when nothing was chosen explicitly: the first one that
  // still needs an answer. Once everything is answered the last step stays
  // open — it holds the receipt and the Checkout button.
  Widget.prototype.defaultOpen = function (st) {
    var order = this.stageOrder;
    for (var i = 0; i < order.length; i++) { var n = order[i]; if (st[n].reachable && !st[n].done) return n; }
    return order[order.length - 1];
  };
  // A step was just completed: fold it and move to the next open question.
  Widget.prototype.advance = function () { this.state.open = null; };

  Widget.prototype.onStageHead = function (name) {
    var st = this.stageStatus(), s = this.state;
    if (!st[name].reachable) return;
    if (s.open === name) { if (!st[name].done) return; s.open = null; }   // fold a re-opened step again
    else s.open = name;
    this.sync();
    track('step_reopened', { step: name });
  };

  // Answered-and-folded steps move their bar up into the pop-up header, under
  // the address, and move back when reopened. Same trick as dockFooter: a
  // comment marks where each one came from. Appending in stageOrder on every
  // run keeps them in step order however they got there.
  Widget.prototype.dockSteps = function () {
    var m = this.modal, self = this;
    if (!m || !m.steps) return;
    this.stageOrder.forEach(function (name) {
      var stage = self.stages[name]; if (!stage) return;
      var head = stage.head, key = '_dockHead_' + name;
      // Only steps BEHIND the open one park at the top. Reopening step 1 with
      // step 2 already answered used to hoist step 2's bar above it, which put
      // the booking in the wrong order on screen.
      var openAt = self.stageOrder.indexOf(self.state.open);
      var park = self.modalOpen &&
        stage.el.classList.contains('is-done') &&
        !stage.el.classList.contains('is-active') &&
        openAt > -1 && self.stageOrder.indexOf(name) < openAt;
      if (park) {
        if (!self[key] && head.parentNode) {
          self[key] = document.createComment('masbk-head');
          head.parentNode.insertBefore(self[key], head);
        }
        m.steps.appendChild(head);
      } else if (self[key] && self[key].parentNode) {
        self[key].parentNode.insertBefore(head, self[key]);
        self[key].parentNode.removeChild(self[key]);
        self[key] = null;
      }
    });
    m.steps.hidden = !m.steps.children.length;
  };

  Widget.prototype.sync = function () {
    var s = this.state, slot = s.selectedSlot, self = this;
    var st = this.stageStatus();
    var open = s.open;
    if (!open || !st[open].reachable) open = this.defaultOpen(st);
    s.open = open;

    // blocks inside the steps
    var peopleDone = this.simple ? !!slot : st.people.done;
    var vis = this.simple ? {
      session: !!s.selectedDate,
      together: false,
      companions: false,
      mode: !!slot && !!this.decideFor(slot),
      decideseats: !!slot && this.effectiveMode() === 'later',
      list: !!slot && this.effectiveMode() === 'now'
    } : {
      session: !!s.selectedDate,
      together: this.togetherApplies(),
      companions: this.togetherApplies() && s.together === true && s.people >= 3,
      mode: peopleDone && !!this.decideFor(slot),
      decideseats: false,
      list: peopleDone && this.effectiveMode() === 'now'
    };
    var revealed = false;
    ['session', 'together', 'companions', 'mode', 'decideseats', 'list'].forEach(function (name) {
      if (self.setBlock(name, vis[name])) {
        revealed = true;
        self.lastRevealed = self.blocks[name];
        if (!self.seen[name]) { self.seen[name] = true; if (name === 'list') track('project_options_visible'); }
      }
    });

    // the steps themselves
    this.stageOrder.forEach(function (name) {
      var stage = self.stages[name], status = st[name], isOpen = name === open;
      stage.el.classList.toggle('is-locked', !status.reachable);
      stage.el.classList.toggle('is-done', status.done);
      stage.el.classList.toggle('is-active', isOpen);
      stage.head.disabled = !status.reachable || (isOpen && !status.done);
      stage.head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var sum = status.done ? self.stageSummary(name) : '';
      stage.sum.textContent = sum;
      stage.sum.hidden = !sum;
      // The step bar asks the question; the body only offers the answers. It
      // drops away the moment there is an answer to show in its place, and it
      // never appears on a product that does not sell the choice.
      if (stage.ask) stage.ask.hidden = !!sum || !status.reachable || !vis.mode;
      stage.edit.hidden = !(status.done && !isOpen);
      self.setBody(stage, isOpen);
    });
    if (this.complete() && !this.seen.summary) { this.seen.summary = true; track('summary_visible'); }
    var moved = open !== this.lastOpen;
    if (moved) this.lastRevealed = null;                 // a new step: bring the step itself into view
    if (moved && this.booted) {
      var stageEl = this.stages[open].el; stageEl.tabIndex = -1;
      try { stageEl.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
    }
    this.lastOpen = open;
    this.renderOfferNudge();
    this.dockSteps();
    this.ui.checkout.disabled = s.busy || !this.complete();
    // The projects step is the last one, so its action bar is the booking's:
    // the offer nudge and Checkout stay put while the list grows or shrinks.
    if (this.modal && this.modal.foot) {
      // The bar carries the offer from the first screen, not just the last, so
      // it stays up the whole way. Checkout is not on offer until there is
      // something to check out, so it waits for the projects step.
      this.modal.foot.hidden = !this.modalOpen;
      var atProjects = open === 'projects';
      this.ui.checkout.hidden = !atProjects;
      if (this.ui.fine) this.ui.fine.hidden = !atProjects;
    }
    if ((moved || revealed) && this.booted) this.follow();
  };

  // Unfold / fold a step body with a height transition.
  Widget.prototype.setBody = function (stage, visible) {
    var body = stage.body;
    var isOpen = !body.hidden && !body.classList.contains('is-closing');
    if (visible === isOpen) return;
    var finish = function () {
      body.style.height = ''; body.style.overflow = ''; body.style.transition = '';
      body.removeEventListener('transitionend', finish);
    };
    if (!this.booted || reducedMotion()) { body.classList.remove('is-closing'); body.hidden = !visible; finish(); return; }
    if (visible) {
      body.classList.remove('is-closing');
      body.hidden = false;
      var h = body.scrollHeight;
      body.style.height = '0px'; body.style.overflow = 'hidden';
      void body.offsetHeight;
      body.style.transition = 'height 0.28s ease';
      body.style.height = h + 'px';
      body.addEventListener('transitionend', finish);
      setTimeout(finish, 360);
    } else {
      body.classList.add('is-closing');
      body.style.height = body.scrollHeight + 'px'; body.style.overflow = 'hidden';
      void body.offsetHeight;
      body.style.transition = 'height 0.24s ease';
      body.style.height = '0px';
      var close = function () {
        if (body.classList.contains('is-closing')) { body.hidden = true; body.classList.remove('is-closing'); }
        finish();
        body.removeEventListener('transitionend', close);
      };
      body.addEventListener('transitionend', close);
      setTimeout(close, 320);
    }
  };

  // Where the page should be scrolled to right now, or null when nothing needs
  // to move. The widget's bottom is kept at the bottom of the screen whenever
  // the open step still fits above it (on a phone that shows the widget and
  // nothing else). Only a step taller than the screen, like the project list
  // or the calendar, is aligned by its bar just under the sticky header.
  Widget.prototype.scrollTarget = function () {
    try {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var mobile = window.innerWidth < 990;
      var headroom = mobile ? 80 : 96, gutter = 16;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var r = this.root.getBoundingClientRect();
      var st = this.stages[this.state.open]; if (!st) return null;
      var s = st.el.getBoundingClientRect();
      var pin = y + r.bottom - (vh - gutter);               // scrollY that puts the widget's bottom at the screen bottom
      var openTopAfter = s.top - (pin - y);                  // where the open step's bar would land
      if (openTopAfter >= headroom) {
        if (mobile) return Math.abs(r.bottom - (vh - gutter)) > 2 ? pin : null;   // phones: always bottom-pinned
        // desktop: the smallest move that shows the whole widget
        if (r.bottom > vh - gutter + 1) return pin;                                 // bottom cut off
        if (r.top < headroom) return r.height <= vh - headroom - gutter ? y + r.top - headroom : pin;   // top cut off
        return null;
      }
      if (Math.abs(s.top - headroom) <= 2) return null;
      return y + s.top - headroom;
    } catch (e) { return null; }
  };

  // Inside the pop-up: where its scroll area should be, or null. A block that
  // just appeared (sessions, the create-together question, the project list)
  // is brought into view without pushing its top out; otherwise the open step
  // is kept fully visible with the smallest move, or its bar goes to the top
  // when the step is taller than the panel.
  Widget.prototype.panelTarget = function () {
    try {
      var panel = this.modal.body, ph = panel.clientHeight;
      if (panel.scrollHeight <= ph + 1) return null;
      var st = panel.scrollTop, pad = 8, pr = panel.getBoundingClientRect();
      var topOf = function (el) { return el.getBoundingClientRect().top - pr.top + st; };
      var stage = this.stages[this.state.open]; if (!stage) return null;
      var block = this.lastRevealed;
      if (block && !block.hidden && stage.el.contains(block)) {
        var bt = topOf(block), bb = bt + block.offsetHeight;
        if (bb > st + ph - pad) return Math.min(bb - ph + pad, bt - pad);
        if (bt - pad < st) return bt - pad;
        return null;
      }
      var s0 = topOf(stage.el), sh = stage.el.offsetHeight;
      if (sh >= ph - pad) return s0 - pad;
      if (s0 - pad < st) return s0 - pad;
      if (s0 + sh + pad > st + ph) return s0 + sh + pad - ph;
      return null;
    } catch (e) { return null; }
  };

  // Follows the fold / unfold transitions frame by frame, so the view moves
  // together with the widget instead of jumping after it. One instant move
  // when the visitor prefers reduced motion. In the pop-up its own scroll
  // area moves; inline (pop-up switched off) the page does.
  Widget.prototype.follow = function () {
    var self = this;
    if (this._followRaf && window.cancelAnimationFrame) cancelAnimationFrame(this._followRaf);
    var inPanel = this.modalOpen && this.modal;
    if (!inPanel && this.cfg.popup) return;              // inline with the pop-up on: the next tap opens it
    var start = Date.now(), dur = reducedMotion() ? 0 : 380;
    var tick = function () {
      if (inPanel) { var t = self.panelTarget(); if (t != null) self.modal.body.scrollTop = Math.max(0, t); }
      else { var y = self.scrollTarget(); if (y != null) window.scrollTo(0, Math.max(0, y)); }
      if (Date.now() - start < dur && window.requestAnimationFrame) self._followRaf = requestAnimationFrame(tick);
      else { self._followRaf = null; self.lastRevealed = null; }
    };
    tick();
  };

  /* ---------- the pop-up ------------------------------------------------ */

  Widget.prototype.buildModal = function () {
    var self = this, text = this.cfg.text;
    var wrap = el('div', 'masbk-modal'); wrap.hidden = true;
    // Carry the offer skin across: the dialog is appended to <body>, so the
    // docked nudge would otherwise lose the .masbk--hw ancestor it is styled by.
    if (this.root.classList.contains('masbk--hw')) wrap.classList.add('masbk-modal--hw');
    var back = el('div', 'masbk-modal__backdrop');
    var dlg = el('div', 'masbk-modal__dialog'); dlg.setAttribute('role', 'dialog'); dlg.setAttribute('aria-modal', 'true'); dlg.tabIndex = -1;
    var head = el('div', 'masbk-modal__head');
    var headrow = el('div', 'masbk-modal__headrow');
    var titles = el('div', 'masbk-modal__titles');
    // The header is sticky, so it is the one place worth spending on what the
    // shopper needs in view the whole way through: what they are booking, and
    // where they would be going. That second line is why there is no longer a
    // review step repeating the address.
    var where = this.cfg.where || {};
    var title = el('div', 'masbk-modal__title', this.cfg.productTitle || text.popupTitle || 'Book your workshop');
    title.id = this.root.id + '-popup-title'; dlg.setAttribute('aria-labelledby', title.id);
    titles.appendChild(title);
    // Just the address, on one line. Not the studio's name (the title above
    // already says which workshop, and every studio is called the same thing)
    // and not a directions link — this is for orienting, not for navigating.
    // The postal address, on one line: street, town, state, ZIP. Not the
    // studio's name — the title already says which workshop, and every studio
    // shares the name — and no directions link. The landmark note ("formerly
    // Echelon Mall") is left off so the ZIP survives on a phone; the full
    // string stays in the tooltip.
    var place = [where.street, where.city].filter(Boolean).join(', ');
    if (place) {
      var sub = el('div', 'masbk-modal__sub', place);
      sub.title = where.full || place;
      titles.appendChild(sub);
    }
    var close = el('button', 'masbk-modal__close'); close.type = 'button'; close.setAttribute('aria-label', text.close || 'Close'); close.innerHTML = '&times;';
    headrow.appendChild(titles); headrow.appendChild(close);
    // Answered steps park here, under the address, and stay there. They cannot
    // do it with position:sticky from inside the list: a folded step's <li> is
    // only as tall as its own bar, so it unsticks the moment it scrolls past.
    var steps = el('div', 'masbk-modal__steps'); steps.hidden = true;
    head.appendChild(headrow); head.appendChild(steps);
    var body = el('div', 'masbk-modal__body');
    // Action bar. The dialog is a flex column, so a third child that does not
    // flex sits outside the scroll area and stays put — the offer and the
    // primary action never move as the project list grows or shrinks.
    var foot = el('div', 'masbk-modal__foot'); foot.hidden = true;
    dlg.appendChild(head); dlg.appendChild(body); dlg.appendChild(foot);
    wrap.appendChild(back); wrap.appendChild(dlg);
    document.body.appendChild(wrap);
    back.addEventListener('click', function () { self.closeModal(); });
    close.addEventListener('click', function () { self.closeModal(); });
    document.addEventListener('keydown', function (e) {
      if (self.modalOpen && e.key === 'Escape') { e.preventDefault(); self.closeModal(); }
    });
    dlg.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var items = qsa(dlg, 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        .filter(function (n) { return n.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    this.modal = { wrap: wrap, dialog: dlg, body: body, foot: foot, close: close, steps: steps };
  };

  // Move the nudge and the primary button between their inline home and the
  // dialog's action bar. A comment marks where each came from, the same trick
  // openModal uses for the widget root itself.
  Widget.prototype.dockFooter = function (on) {
    var m = this.modal, self = this;
    if (!m || !m.foot) return;
    [['_dockNudge', this.ui.offerNudge], ['_dockCont', this.ui.checkout], ['_dockFine', this.ui.fine]].forEach(function (pair) {
      var key = pair[0], node = pair[1];
      if (!node) return;
      if (on) {
        if (!self[key] && node.parentNode) {
          self[key] = document.createComment('masbk-dock');
          node.parentNode.insertBefore(self[key], node);
        }
        m.foot.appendChild(node);
      } else if (self[key] && self[key].parentNode) {
        self[key].parentNode.insertBefore(node, self[key]);
        self[key].parentNode.removeChild(self[key]);
        self[key] = null;
      }
    });
  };

  Widget.prototype.openModal = function () {
    if (this.modalOpen) return;
    if (!this.modal) this.buildModal();
    var self = this, m = this.modal;
    this.placeholder = document.createComment('mas-booking');
    this.root.parentNode.insertBefore(this.placeholder, this.root);
    m.body.appendChild(this.root);
    this.dockFooter(true);
    // lock the page behind (position: fixed keeps iOS from scrolling it)
    this._lockY = window.pageYOffset || 0;
    document.body.style.top = (-this._lockY) + 'px';
    document.documentElement.classList.add('masbk-lock');
    m.wrap.hidden = false;
    void m.wrap.offsetWidth;
    m.wrap.classList.add('is-open');
    this.modalOpen = true;
    m.body.scrollTop = 0;
    this.dockSteps();
    try { m.dialog.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
    setTimeout(function () { self.follow(); }, 30);
    track('booking_popup_opened');
  };

  Widget.prototype.closeModal = function () {
    if (!this.modalOpen) return;
    var self = this, m = this.modal;
    this.modalOpen = false;
    m.wrap.classList.remove('is-open');
    var finish = function () {
      m.wrap.hidden = true;
      self.dockFooter(false);
      if (self.placeholder && self.placeholder.parentNode) {
        self.placeholder.parentNode.insertBefore(self.root, self.placeholder);
        self.placeholder.parentNode.removeChild(self.placeholder);
      }
      self.placeholder = null;
      // Answered steps park in the pop-up's header while it is open. Closing it
      // has to hand them back, or they stay inside a hidden dialog and the
      // widget on the page loses the date the shopper already chose.
      self.dockSteps();
    };
    document.documentElement.classList.remove('masbk-lock');
    document.body.style.top = '';
    window.scrollTo(0, this._lockY || 0);
    if (reducedMotion()) finish(); else setTimeout(finish, 220);
    track('booking_popup_closed');
  };

  /* ---------- submit: reserve -> cart attributes -> cart lines -> checkout */

  Widget.prototype.submit = function () {
    if (this.state.busy || !this.complete()) return;
    var self = this, slot = this.state.selectedSlot, tz = this.tz;
    var picks = this.lines();
    if (!picks.length || !slot) return;
    this.setError(null); this.setBusy(true); this.ui.checkout.disabled = true;
    track('checkout_clicked', { booking_start: slot.start, people: this.peopleCount(), companions: this.companions(), project_mode: this.effectiveMode() });

    var cart, cartToken;
    cartGet().then(function (c) {
      cart = c || {};
      var attrs = cart.attributes || {};
      var existing = attrs['BTA Token'] || cart.token || '';
      // BTA generates its own token for a fresh cart; the reservation endpoint
      // hands back the canonical one, which we then store on the cart.
      return existing ? existing : sha256Hex(self.cfg.shop + '-' + self.cfg.productId + '-' + new Date() + '-' + Math.random());
    }).then(function (token) {
      cartToken = token;
      // Re-reserve BTA items already in the cart so the hold covers the whole order (mirrors BTA's widget).
      var items = [];
      (cart.items || []).forEach(function (li) {
        var p = li.properties || {};
        if (p._date_iso8601) items.push({ externalId: li.variant_id, quantity: li.quantity, location_name: null, start: p._date_iso8601 });
      });
      picks.forEach(function (pk) { items.push({ externalId: pk.id, quantity: pk.quantity, location_name: null, start: slot.start }); });
      return self.client.reserve(cartToken, items);
    }).then(function (res) {
      var reservation = res && res.reservation;
      if (!reservation || !reservation.items || reservation.items.some(function (i) { return i.status !== 'ok'; })) {
        throw Object.assign(new Error('reservation not ok'), { data: res, soft: true });
      }
      var newToken = reservation.cart_token || cartToken;
      return cartSetAttributes({ 'BTA Token': newToken, 'BookThatApp Reservation Token': null }).then(function () { return reservation; });
    }).then(function () {
      // Add each variant as its own line, sequentially, with BTA's line-item properties.
      var chain = Promise.resolve();
      picks.forEach(function (pk) {
        chain = chain.then(function () {
          return cartAdd({
            id: pk.id, quantity: pk.quantity,
            properties: {
              'Date': btaDate(slot.start, tz),
              'Time': btaTime(slot.start, tz),
              '_date_iso8601': slot.start,
              '_addons_uuid': uuid()
            }
          });
        });
      });
      return chain;
    }).then(function () {
      track('booking_added_to_cart', { booking_start: slot.start, people: self.peopleCount(), companions: self.companions(), project_mode: self.effectiveMode() });
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var dest = root + (self.cfg.redirect === 'cart' ? 'cart' : 'checkout');
      window.EbruBooking.navigate(dest);
    }).catch(function (e) {
      self.setBusy(false);
      var msg = self.cfg.text.reserveError;
      var d = e && e.data;
      if (d) {
        var errs = [];
        if (Array.isArray(d.errors)) errs = errs.concat(d.errors);
        else if (d.errors && typeof d.errors === 'object') Object.keys(d.errors).forEach(function (k) { errs.push(String(d.errors[k])); });
        if (d.error_items && Array.isArray(d.error_items)) d.error_items.forEach(function (it) { if (it && it.message) errs.push(it.message); });
        if (d.description) errs.push(d.description);
        if (errs.length) msg = errs.join(' ');
        else if (e.soft || (d.reservation && d.reservation.items)) msg = self.cfg.text.seatsGone;
      }
      self.setError(msg);
      if (window.console) console.error('[MAS booking] submit failed', e);
      // Availability may have changed; refresh this month silently and re-check the answers.
      var v = self.state.view; delete self.monthCache[self.monthKey(v.year, v.month)];
      Object.keys(self.slotsByDate).forEach(function (k) { if (k.indexOf(self.monthKey(v.year, v.month)) === 0) delete self.slotsByDate[k]; });
      self.loadMonth(v.year, v.month).then(function () {
        var fresh = (self.slotsByDate[self.state.selectedDate] || []).filter(function (s) { return s.id === slot.id; })[0];
        self.state.selectedSlot = fresh || null;
        if (fresh) self.clampToSlot();
        self.renderCalendar();
        self.renderBelowSession();
        self.sync();
      }).catch(function () { /* keep UI as-is */ });
    });
  };

  /* ---------- boot ------------------------------------------------------ */

  window.EbruBooking = window.EbruBooking || {};
  window.EbruBooking.navigate = window.EbruBooking.navigate || function (url) { window.location.href = url; };
  window.EbruBooking.instances = [];

  function hideBtaWidget() {
    // The BTA App Embed injects its own iframe widget into the product form.
    // When our widget is active we keep the embed (so BTA keeps syncing orders
    // to bookings) but remove its UI so shoppers only see one calendar.
    window.__btaWidgetsLoaderAlreadyRunning = true; // BTA's loader checks this first and bails out
    var kill = function () {
      qsa(document, '#bta-product-widget, .bta-widget-modal-back, .widget-loading-indicator').forEach(function (n) { n.parentNode && n.parentNode.removeChild(n); });
      qsa(document, 'form[action*="/cart/add"]').forEach(function (f) { f.classList.add('bta-skip-loading-indicator'); });
    };
    kill();
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () { kill(); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  function boot() {
    qsa(document, '[data-ebru-booking]').forEach(function (root) {
      if (root.__ebruBooking) return;
      try {
        root.__ebruBooking = new Widget(root);
        window.EbruBooking.instances.push(root.__ebruBooking);
        if (root.getAttribute('data-hide-bta') === 'true') hideBtaWidget();
      } catch (e) { if (window.console) console.error('[Ebru booking] init failed', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
